import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import App from "@/App";
import "@/index.css";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — application entry point.
//
// This file has exactly one job: attach the React tree to the
// DOM. It contains no routing, no providers, no store
// initialisation, no error boundary. Those belong in App.tsx,
// where they can be reasoned about in context.
//
// If this file grows past ~25 lines, something has been put in
// the wrong place.
// ─────────────────────────────────────────────────────────────────

/**
 * Resolve the mount node once, at module scope, and fail loudly
 * if it's missing.
 *
 * Why not `document.getElementById("root")!`?
 *
 *   • The non-null assertion silences the compiler without
 *     proving anything. If the HTML ever changes id, the app
 *     crashes with "Cannot read properties of null" deep inside
 *     React's internals — a miserable stack trace to debug.
 *
 *   • Throwing here produces an error at the exact line that
 *     knows what went wrong, with a message that names the
 *     missing id. The five extra lines pay for themselves the
 *     first time someone renames the div in index.html and
 *     forgets this file.
 */
const container = document.getElementById("root");

if (!container) {
  throw new Error(
    "[Iratation High OS] Mount node #root not found. " +
      "Check that index.html still contains <div id=\"root\"></div>."
  );
}

/**
 * React 18's createRoot is the concurrent renderer. Called once,
 * here, and never again — calling it twice on the same container
 * warns and produces subtle bugs, so this is the only place it
 * exists.
 */
const root = createRoot(container);

root.render(
  /**
   * StrictMode is intentionally on, even in the production build.
   *
   * The usual objection is that StrictMode double-invokes effects
   * in development, which can be noisy. For this app, that noise
   * is exactly the point:
   *
   *   • Framer Motion's layout animations register and unregister
   *     measurements in effects. If we double-invoke and the
   *     animation still looks right, the effect is idempotent.
   *     If it glitches, we found a real bug before a director
   *     did.
   *
   *   • The tenant and role state lives in a Zustand store.
   *     StrictMode forces us to write selectors that don't
   *     mutate on read, which is the correct pattern anyway.
   *
   * The cost is a doubled render pass in development. The
   * benefit is a codebase that behaves identically under the
   * concurrent renderer that React actually ships in production.
   */
  <StrictMode>
    <App />
  </StrictMode>
);